import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Navigate, useLocation, useParams, type RouteObject } from 'react-router-dom';
import { listBoards } from '../../api/boards';
import { Button, EmptyState, PageHeader } from '../../components/shared';
import { getBoardsLang, subscribeBoardsLang, tBoards, type BoardsI18nKey } from '../../lib/boards-i18n';
import {
  BOARD_ROOT, boardListPath, parseNumericId,
  pickDefaultBoardId,
} from './board-v1-url';
import { BoardListScreen } from './list/BoardListScreen';
import { BoardPostScreen } from './post/BoardPostScreen';
import { BoardWriteScreen } from './write/BoardWriteScreen';
import { BoardSettingsV1Screen } from './settings/BoardSettingsV1Screen';
import { BoardShell } from './BoardShell';

/**
 * 게시판 v1 라우트 (U1 · 2026-09-15) — `VITE_BOARD_V1='1'` 일 때만 라우터에 선다.
 *
 * 이 단위는 **주소의 뼈대**만 세운다. 화면(목록 U2 · 글 보기 U3 · 쓰기 U4 · 설정 U5)은 뒤 단위가
 * `BoardV1Screen` 자리를 하나씩 바꿔 끼운다 — 그때까지는 «어느 화면 · 어느 id · 어느 탭»만 보인다.
 *
 * ★ 옛 주소가 깨지지 않게:
 *   - `/board` 는 **기본 회사 게시판 목록**으로 옮긴다(지금 화면이 처음 여는 게시판과 같은 규칙).
 *     쿼리(`?q=`)는 그대로 들고 간다.
 *   - `/board/12`(공지 슬라이드 `linkUrl` 자리표시 모양)는 게시판 id 로 읽는다.
 *   - 숫자가 아닌 id(`/board/abc` · `/board/post`)는 `/board` 로 되돌린다 — 404 로 끝내지 않는다.
 *   - `/board/notice/:id` · `/board/comment/:id` 는 **알림 딥링크 리졸버 자리**다. 서버가 위치를
 *     알려 주기 전(R2 · R12)에는 `/board` 로 보낸다 — 지금 알림이 가는 곳과 같다.
 */

function useBoardsLang() {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang);
}

type HomeState =
  | { kind: 'loading' }
  | { kind: 'go'; boardId: string }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

/** `/board` — 기본 게시판을 찾아 그 목록으로 `replace`. */
export function BoardHomeRedirect() {
  useBoardsLang();
  const location = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<HomeState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    listBoards()
      .then((res) => {
        if (cancelled) return;
        const boardId = pickDefaultBoardId(res.boards);
        setState(boardId ? { kind: 'go', boardId } : { kind: 'empty' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    return () => { cancelled = true; };
  }, [attempt]);

  if (state.kind === 'go') {
    return <Navigate replace to={`${boardListPath(state.boardId)}${location.search}`} />;
  }
  if (state.kind === 'empty') {
    return <EmptyState kind="first-run" title={tBoards('boards.empty')} />;
  }
  if (state.kind === 'error') {
    return (
      <EmptyState
        kind="error"
        title={tBoards('boards.v1.load_failed')}
        description={state.message || undefined}
        action={(
          <Button variant="secondary" size="compact" onClick={() => setAttempt((n) => n + 1)}>
            {tBoards('boards.v1.retry')}
          </Button>
        )}
      />
    );
  }
  return <div className="board-v1-loading" aria-busy="true">{tBoards('boards.v1.loading')}</div>;
}

/** 주소 id 를 거른다 — 숫자가 아니면 `/board` 로. */
function WithNumericParam({ name, children }: { name: string; children: (id: string) => ReactNode }) {
  const params = useParams();
  const id = parseNumericId(params[name]);
  if (!id) return <Navigate replace to={BOARD_ROOT} />;
  return <>{children(id)}</>;
}

export type BoardV1ScreenKind = 'list' | 'trash' | 'write' | 'settings' | 'post' | 'edit';

const SCREEN_TITLE: Record<BoardV1ScreenKind, BoardsI18nKey> = {
  list: 'boards.v1.screen.list',
  trash: 'boards.v1.screen.trash',
  write: 'boards.v1.screen.write',
  settings: 'boards.v1.screen.settings',
  post: 'boards.v1.screen.post',
  edit: 'boards.v1.screen.edit',
};

/**
 * 화면 자리 — U2~U5 가 이 자리를 실제 화면으로 바꾼다.
 * `data-*` 로 «어느 화면 · id · 탭»을 남겨 라우트 검사가 읽는다.
 */
export function BoardV1Screen({ screen, id, tab }: { screen: BoardV1ScreenKind; id: string; tab?: string }) {
  useBoardsLang();
  const title = tBoards(SCREEN_TITLE[screen]);
  return (
    <div className="board-v1" data-board-screen={screen} data-id={id} data-tab={tab}>
      <PageHeader breadcrumb={[tBoards('boards.v1.crumb'), title]} title={title} />
      <p className="board-v1__placeholder">{tBoards('boards.v1.placeholder')}</p>
    </div>
  );
}

/** 목록 · 휴지통 — U2 화면(`list/BoardListScreen`). 같은 게시판 id 면 트리 · 숫자 칸을 새로 그리지 않게 한 화면이 두 모습을 갖는다. */
function ListRoute({ mode }: { mode: 'list' | 'trash' }) {
  return (
    <WithNumericParam name="boardId">
      {(id) => <BoardListScreen boardId={id} mode={mode} />}
    </WithNumericParam>
  );
}

/** 글 보기 — U3 화면(`post/BoardPostScreen`). 탭은 그 화면이 주소에서 읽는다. */
function PostRoute() {
  return (
    <WithNumericParam name="postId">
      {(id) => <BoardPostScreen postId={id} />}
    </WithNumericParam>
  );
}

/** 게시판 설정 — U5 화면(`settings/BoardSettingsV1Screen`). 탭은 그 화면이 주소에서 읽는다. */
function SettingsRoute() {
  return (
    <WithNumericParam name="boardId">
      {(id) => <BoardSettingsV1Screen boardId={id} />}
    </WithNumericParam>
  );
}

export type BoardLinkKind = 'notice' | 'comment';

/**
 * 알림 딥링크 → 글 주소. 서버 요청 R2(공지 id → 글 id) · R12(댓글 id → 글 id)가 생기면 여기서 부른다.
 * 지금은 모른다(null) — 부르는 쪽은 `/board` 로 보낸다.
 */
export async function resolveBoardLink(kind: BoardLinkKind, id: string): Promise<string | null> {
  void kind;
  void id;
  return null;
}

export function BoardLinkResolver({ kind }: { kind: BoardLinkKind }) {
  useBoardsLang();
  const params = useParams();
  const id = parseNumericId(params[kind === 'notice' ? 'noticeId' : 'commentId']);
  const [target, setTarget] = useState<string | null | undefined>(id ? undefined : null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    resolveBoardLink(kind, id)
      .then((to) => { if (!cancelled) setTarget(to); })
      .catch(() => { if (!cancelled) setTarget(null); });
    return () => { cancelled = true; };
  }, [kind, id]);

  if (target === undefined) return <div className="board-v1-loading" aria-busy="true">{tBoards('boards.v1.loading')}</div>;
  return <Navigate replace to={target ?? BOARD_ROOT} />;
}

/** 라우터에 끼우는 `board` 라우트 — `router/index.tsx` 가 플래그로 고른다. */
export const boardV1Route: RouteObject = {
  path: 'board',
  /**
   * 2026-09-17 사용자 지시 — 좌측 메뉴(게시판 트리)를 **모든 하위 화면**에서 세운다.
   * 종전에는 트리가 목록 화면 «안»에만 있어 글 상세·글쓰기·설정으로 가면 사라졌다.
   * 셸은 `<Outlet/>` 밖에 있으므로 아래 children 이 바뀌어도 좌측은 그대로 선다.
   */
  element: <BoardShell />,
  children: [
    { index: true, element: <BoardHomeRedirect /> },
    // 정적 세그먼트(`post` · `notice` · `comment`)가 `:boardId` 보다 먼저 맞는다(react-router 6 순위).
    { path: 'post/:postId', element: <PostRoute /> },
    // 글 수정 · 글쓰기 — U4 화면(`write/BoardWriteScreen`). 같은 화면이 두 모습을 갖는다(값 · 규칙이 같다).
    {
      path: 'post/:postId/edit',
      element: <WithNumericParam name="postId">{(id) => <BoardWriteScreen mode="edit" postId={id} />}</WithNumericParam>,
    },
    { path: 'notice/:noticeId', element: <BoardLinkResolver kind="notice" /> },
    { path: 'comment/:commentId', element: <BoardLinkResolver kind="comment" /> },
    { path: ':boardId', element: <ListRoute mode="list" /> },
    { path: ':boardId/trash', element: <ListRoute mode="trash" /> },
    {
      path: ':boardId/write',
      element: <WithNumericParam name="boardId">{(id) => <BoardWriteScreen mode="write" boardId={id} />}</WithNumericParam>,
    },
    { path: ':boardId/settings', element: <SettingsRoute /> },
    { path: '*', element: <Navigate replace to={BOARD_ROOT} /> },
  ],
};
